import type { ModelData } from '@/types/models';
import { formatCurrency } from '@/utils/formatters';

interface ValuationTabProps {
  data: ModelData;
}

export function ValuationTab({ data }: ValuationTabProps) {
  const valuation = data?.valuation;
  const revenue = data?.revenue || [];

  // Handle case where data is not yet loaded
  if (!valuation) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="text-4xl mb-3">💎</div>
          <p className="text-slate-500">No valuation data available</p>
        </div>
      </div>
    );
  }

  // Get Year 5 revenue for context
  const year5Revenue = revenue[revenue.length - 1]?.total || 0;

  // Get valuation values with fallbacks
  const revenueMultiple = valuation.revenueMultiple || valuation.revenueMultipleLow || 5;
  const revenueBasedValuation = valuation.revenueBasedValuation || valuation.impliedValuationLow || year5Revenue * revenueMultiple;
  const dcfValuation = valuation.dcfValuation || valuation.enterpriseValue || 0;
  const recommendedValuation = valuation.recommendedValuation || revenueBasedValuation;

  return (
    <div className="space-y-8">
      {/* Valuation Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Revenue Multiple Valuation */}
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-6 rounded-xl text-white shadow-lg">
          <div className="text-blue-100 text-sm font-medium mb-1">Revenue Multiple Valuation</div>
          <div className="text-3xl font-bold mb-2">
            {formatCurrency(revenueBasedValuation)}
          </div>
          <div className="text-blue-200 text-sm">
            {revenueMultiple}x Revenue Multiple
          </div>
        </div>

        {/* DCF Valuation */}
        <div className={`p-6 rounded-xl shadow-lg ${
          dcfValuation >= 0
            ? 'bg-gradient-to-br from-green-500 to-green-600 text-white'
            : 'bg-gradient-to-br from-orange-500 to-orange-600 text-white'
        }`}>
          <div className={`text-sm font-medium mb-1 ${
            dcfValuation >= 0 ? 'text-green-100' : 'text-orange-100'
          }`}>DCF Valuation</div>
          <div className="text-3xl font-bold mb-2">
            {formatCurrency(dcfValuation)}
          </div>
          <div className={`text-sm ${
            dcfValuation >= 0 ? 'text-green-200' : 'text-orange-200'
          }`}>
            Discounted Cash Flow Analysis
          </div>
        </div>

        {/* Recommended Valuation */}
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-6 rounded-xl text-white shadow-lg">
          <div className="text-purple-100 text-sm font-medium mb-1">Recommended Valuation</div>
          <div className="text-3xl font-bold mb-2">
            {formatCurrency(recommendedValuation)}
          </div>
          <div className="text-purple-200 text-sm">
            Best Estimate
          </div>
        </div>
      </div>

      {/* Detailed Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Revenue Multiple Analysis */}
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
            <h3 className="font-semibold text-slate-800">📊 Revenue Multiple Analysis</h3>
          </div>
          <div className="p-4">
            <table className="w-full text-sm">
              <tbody className="divide-y divide-slate-100">
                <tr>
                  <td className="py-2 text-slate-600">Year 5 Revenue</td>
                  <td className="py-2 text-right font-semibold text-slate-900">
                    {formatCurrency(year5Revenue)}
                  </td>
                </tr>
                <tr>
                  <td className="py-2 text-slate-600">Revenue Multiple</td>
                  <td className="py-2 text-right font-semibold text-slate-900">
                    {revenueMultiple}x
                  </td>
                </tr>
                <tr className="bg-blue-50">
                  <td className="py-2 text-blue-800 font-medium">Implied Valuation</td>
                  <td className="py-2 text-right font-bold text-blue-900">
                    {formatCurrency(revenueBasedValuation)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Valuation Context */}
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
            <h3 className="font-semibold text-slate-800">💡 Valuation Context</h3>
          </div>
          <div className="p-4 space-y-4">
            <p className="text-sm text-slate-600">
              This valuation is based on industry-standard multiples for similar companies 
              at comparable growth stages. Key factors that impact valuation include:
            </p>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-0.5">✓</span>
                <span>Revenue growth trajectory and predictability</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-0.5">✓</span>
                <span>Gross margin and unit economics</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-0.5">✓</span>
                <span>Customer retention and expansion rates</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-0.5">✓</span>
                <span>Market size and competitive position</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
